import type { OrganizationEngageSettings } from '@care-portals/api-interfaces';

/**
 * Pathwai ("Engage") routing helpers.
 *
 * During the patient-web decommission, an organization moves questionnaires over
 * to Pathwai one key at a time via `organization.engage.qnr`. Once a key is
 * listed, patient-web stops rendering it and hands the patient to Pathwai's
 * CarePortals entry page, which works out the rest for itself.
 *
 * Note there is only one URL to build. CarePortals does not need to know which
 * Pathwai flow answers which questionnaire — Pathwai matches questionnaire keys
 * to its own flow slugs — so nothing here has to change as flows are added, and
 * the entry point keeps working after patient-web is gone.
 */

/**
 * Shared runner fallback when an org has no `newPatientPortalDomain`.
 * Includes `/engage` to match the CarePortals runner `basePath`.
 */
export const PATHWAI_RUNNER_DEFAULT_BASE_URL = 'https://flow.portals.care/engage';

/**
 * Subpath on the patient portal where patient-next reverse-proxies the runner.
 * Must match Pathwai `NEXT_PUBLIC_BASE_PATH` / patient-next rewrites.
 */
export const PATHWAI_LOGICAL_RUNNER_BASE_PATH = '/engage';

/** Path of the Pathwai entry point that mirrors patient-web's /consultation/start. */
export const PATHWAI_START_PATH = '/integrations/careportals/start';

/** Which engine answers a given questionnaire for this organization. */
export type ConsultationEngine = 'legacy' | 'pathwai';

function normalizeBaseUrl(baseUrl: string | undefined): string {
  const raw = (baseUrl || '').trim() || PATHWAI_RUNNER_DEFAULT_BASE_URL;
  return raw.replace(/\/+$/, '');
}

/**
 * Public runner base for a patient portal host (logical domain), e.g.
 * `https://app.clinic.com/engage`. Returns undefined when the host is empty/invalid.
 */
export function buildLogicalRunnerBaseUrl(
  portalDomainOrUrl: string | undefined | null,
): string | undefined {
  if (typeof portalDomainOrUrl !== 'string') return undefined;
  const raw = portalDomainOrUrl.trim();
  if (!raw) return undefined;
  try {
    const hostname = (raw.includes('://') ? new URL(raw) : new URL(`https://${raw}`))
      .hostname.trim()
      .toLowerCase()
      .replace(/\.$/, '');
    if (!hostname) return undefined;
    return `https://${hostname}${PATHWAI_LOGICAL_RUNNER_BASE_PATH}`;
  } catch {
    return undefined;
  }
}

/**
 * Portal host used to build `{host}/engage`.
 *
 * Layout first, then the top-level field. Never the legacy Angular
 * `patientPortalDomain` — Engage is reverse-proxied on patient-next only.
 */
export function engagePatientPortalDomain(orgConfig?: {
  newPatientPortalDomain?: string | null;
  patientWebAppLayout?: { newPatientPortalDomain?: string | null } | null;
} | null): string | undefined {
  const fromLayout = orgConfig?.patientWebAppLayout?.newPatientPortalDomain?.trim();
  if (fromLayout) return fromLayout;
  const fromTop = orgConfig?.newPatientPortalDomain?.trim();
  return fromTop || undefined;
}

/**
 * Resolve the public runner base used for `/o/...`, `/f/...`, and CarePortals start links.
 * Always `{newPatientPortalDomain}/engage`, else the shared flow.host default.
 */
export function resolveEngageRunnerBaseUrl(
  portalDomainOrUrl?: string | null,
): string {
  return (
    buildLogicalRunnerBaseUrl(portalDomainOrUrl) || PATHWAI_RUNNER_DEFAULT_BASE_URL
  );
}

/**
 * True when `engage` is configured well enough to route traffic — a Pathwai
 * organization id is required, otherwise no URL can be built.
 */
export function isEngageConfigured(
  engage: OrganizationEngageSettings | undefined
): boolean {
  return !!engage?.orgId?.trim();
}

/** True when this questionnaire key should be answered in Pathwai. */
export function isPathwaiQuestionnaire(
  engage: OrganizationEngageSettings | undefined,
  type: string
): boolean {
  if (!isEngageConfigured(engage)) return false;
  return (engage?.qnr || []).includes(type);
}

export function getConsultationEngine(
  engage: OrganizationEngageSettings | undefined,
  type: string
): ConsultationEngine {
  return isPathwaiQuestionnaire(engage, type) ? 'pathwai' : 'legacy';
}

export interface PathwaiStartUrlOptions {
  /** Order whose outstanding questionnaires should be run. */
  orderId?: string | number;
  /** Explicit questionnaire keys, when not driven by an order. */
  flows?: string[];
  /** Where to land once every questionnaire is complete. */
  redirectTo?: string;
}

/**
 * URL of the Pathwai entry point for this organization.
 *
 * Deliberately mirrors patient-web's `/consultation/start` query contract so a
 * link can be repointed from one to the other without rewriting its params.
 */
export function buildPathwaiStartUrl(
  engage: OrganizationEngageSettings | undefined,
  options?: PathwaiStartUrlOptions & {
    /** `newPatientPortalDomain` host/URL — `{host}/engage`, else the shared default. */
    portalDomain?: string | null;
  }
): string | undefined {
  const orgId = engage?.orgId?.trim();
  if (!orgId) return undefined;

  const params = new URLSearchParams();
  if (options?.orderId !== undefined && options.orderId !== null && `${options.orderId}`) {
    params.set('orderId', `${options.orderId}`);
  }
  if (options?.flows?.length) {
    params.set('flows', options.flows.join(','));
  }
  if (options?.redirectTo) {
    params.set('redirectTo', options.redirectTo);
  }
  // The runner resolves the workspace from its own host when it is served on the
  // portal's domain; sending it explicitly keeps the shared runner domain working.
  params.set('org', orgId);

  const base = resolveEngageRunnerBaseUrl(options?.portalDomain);
  return `${normalizeBaseUrl(base)}${PATHWAI_START_PATH}?${params.toString()}`;
}
